/// <reference types="@types/google.maps" />

import { SignupFormAddressValue } from '@max/common/setpage';
import * as Popover from '@radix-ui/react-popover';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FaMapMarkerAlt, FaTimesCircle } from 'react-icons/fa';
import styled from 'styled-components';
import { useDebounce } from 'use-debounce';
import { useEnvContext, useHostContext } from '../../contexts';
import { TextInput } from './text-input';

const ClearButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: none;
  outline: none;
  border: 0;
  width: 30px;
  flex-shrink: 0;
`;

const PopoverContent = styled(Popover.Content)`
  width: var(--radix-popper-anchor-width);
  max-height: calc(var(--radix-popover-content-available-height) + 10px - 10px);
  border-radius: 6px;
  background: var(--set-page-white);
  color: var(--set-page-black);
  z-index: var(--z-index-modal-content);
  box-shadow:
    0px 10px 38px -10px rgba(22, 23, 24, 0.35),
    0px 10px 20px -15px rgba(22, 23, 24, 0.2);
  overflow: auto;
  padding: 8px 0;
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  cursor: pointer;

  &:hover {
    outline: none;
    background: #fafafa;
  }
`;

const ListItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

const ListItemLabel = styled.div`
  font-size: 14px;
  font-family: var(--set-page-base-font-family);
  font-weight: 600;
  line-height: 1;
  color: var(--set-page-black);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const ListItemCaption = styled.div`
  font-size: 13px;
  font-family: var(--set-page-base-font-family);
  line-height: 1;
  color: gray;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const MANUAL_ENTRY_ID = 'manual_entry';

interface Option {
  label: string;
  caption: string;
  placeId: string;
}

interface GoogleMapsAutocompleteInputProps {
  id?: string;
  name?: string;
  value?: SignupFormAddressValue | null;
  placeholder?: string;
  isInvalid?: boolean;
  onBlur?: () => void;
  onChange?: (value: SignupFormAddressValue | null) => void;
}

export const GoogleMapsAutocompleteInput = ({
  id,
  name,
  value,
  placeholder,
  isInvalid,
  onBlur,
  onChange,
}: GoogleMapsAutocompleteInputProps) => {
  const host = useHostContext();
  const env = useEnvContext();
  const ref = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<Option[]>([]);
  const [inputValue, setInputValue] = useState(value?.address ?? '');
  const [query] = useDebounce(inputValue, 600);

  const onSelect = useCallback(
    async (option: Option) => {
      if (!env.mapsUrl || !env.userToken) {
        return;
      }

      if (option.placeId === MANUAL_ENTRY_ID) {
        onChange?.({
          address: option.label,
          placeId: option.placeId,
          lat: 0,
          lng: 0,
        });

        return ref.current?.blur();
      }

      const url = new URL(
        '/maps/api/geocode/json',
        env.mapsUrl.replace(/\/$/, ''),
      );

      url.searchParams.set('place_id', option.placeId);

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${env.userToken}`,
        },
      });

      const json = (await response.json()) as {
        data?: {
          results?: google.maps.GeocoderResult[];
        };
      };

      if (json.data?.results?.length) {
        const result = json.data.results[0];

        onChange?.({
          address: result.formatted_address,
          placeId: result.place_id,
          lat: result.geometry.location.lat as unknown as number,
          lng: result.geometry.location.lng as unknown as number,
        });

        ref.current?.blur();
      }
    },
    [env.mapsUrl, env.userToken, onChange],
  );

  useEffect(() => {
    if (!isOpen) {
      setInputValue(value?.address ?? '');
    }
  }, [isOpen, value?.address]);

  useEffect(() => {
    const handler = async () => {
      if (!isOpen || !env.mapsUrl || !env.userToken || host.isPhonePreview) {
        return;
      }

      if (!query) {
        return setOptions([]);
      }

      const url = new URL(
        '/maps/api/place/autocomplete/json',
        env.mapsUrl.replace(/\/$/, ''),
      );

      url.searchParams.set('input', query);

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${env.userToken}`,
        },
      });

      const json = (await response.json()) as {
        data?: {
          predictions?: google.maps.places.AutocompletePrediction[];
        };
      };

      if (json.data?.predictions?.length) {
        setOptions(
          json.data.predictions
            ?.filter((r) => !!r.structured_formatting.secondary_text)
            ?.map((c) => ({
              label: c.structured_formatting.main_text,
              caption: c.structured_formatting.secondary_text,
              placeId: c.place_id,
            })),
        );
      } else {
        setOptions([
          {
            label: query,
            caption: '',
            placeId: MANUAL_ENTRY_ID,
          },
        ]);
      }
    };

    handler();
  }, [env.mapsUrl, env.userToken, host.isPhonePreview, isOpen, query]);

  return (
    <Popover.Root open={isOpen && !!options.length}>
      <Popover.Anchor>
        <TextInput
          ref={ref}
          id={id}
          name={name}
          value={inputValue}
          placeholder={placeholder ?? 'Type an address'}
          isInvalid={isInvalid}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onBlur={() => {
            onBlur?.();
            setIsOpen(false);
            setOptions([]);
          }}
          rightAddon={
            value && (
              <ClearButton onClick={() => onChange?.(null)}>
                <FaTimesCircle color="black" />
              </ClearButton>
            )
          }
        />
      </Popover.Anchor>
      <Popover.Portal>
        <PopoverContent
          sideOffset={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onFocusOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onMouseDown={(e) => e.preventDefault()}
        >
          {options.map((option) => (
            <ListItem key={option.placeId} onClick={() => onSelect(option)}>
              <FaMapMarkerAlt color="gray" style={{ flexShrink: 0 }} />
              <ListItemContent>
                <ListItemLabel>{option.label}</ListItemLabel>
                {option.caption && (
                  <ListItemCaption>{option.caption}</ListItemCaption>
                )}
              </ListItemContent>
            </ListItem>
          ))}
        </PopoverContent>
      </Popover.Portal>
    </Popover.Root>
  );
};
